<script setup>
import { isEmpty, isNull } from 'lodash'
import { computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ElementProgressApiHandler from '@/api/element-progress'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import {
  ELEMENT_PROGRESS_STATUSES,
  getProgressTableHeaderCellDisplayValue
} from '@/utils/element-progress'
import { handleError } from '@/utils/error-handling'
import {
  findProgressAggregationOptionsByTypeId,
  THRESHOLD_PERIOD_GRANULATION,
  THRESHOLD_PROGRESS_AGGREGATIONS
} from '@/utils/okr-element-values'
import { getGradeColorVariable, getGradeDisplayValue } from '@/utils/okr-elements/grade'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'

import AppDroplist from '@/components/AppDroplist'
import AppHrefLink from '@/components/AppHrefLink'
import AppInfoMessage from '@/components/ui/AppInfoMessage'

defineOptions({
  name: 'ProgressDescription'
})

const props = defineProps({
  elementId: {
    type: [String, Number],
    required: true
  },

  grade: {
    type: [String, Number],
    required: true
  },

  gradeColor: {
    type: String,
    required: true
  },

  progressAggregationTypeId: {
    type: Number,
    required: true,
    validator: v =>
      Object.values(THRESHOLD_PROGRESS_AGGREGATIONS)
        .map(item => item.value)
        .includes(v)
  },

  granulationId: {
    type: Number,
    required: true,
    validator: v =>
      Object.values(THRESHOLD_PERIOD_GRANULATION)
        .map(item => item.value)
        .includes(v)
  },

  disabled: {
    type: Boolean
  }
})

const isLoading = ref(false)

const progressDataList = ref([])

const isNoValidPeriods = computed(() => {
  return !isLoading.value && isEmpty(progressDataList.value)
})

const normalizeProgressData = ({ progressData = [], periods = [] } = {}) => {
  return progressData.reduce((acc, item, index) => {
    const { data, ...rest } = item

    if (data && isNull(data.value)) {
      return acc
    }

    const { name = '' } = periods[index] || {}

    return [
      ...acc,
      {
        ...data,
        ...rest,
        name
      }
    ]
  }, [])
}

const onDropdownClose = () => {
  progressDataList.value = []
}

const getElementProgress = async () => {
  const api = new ElementProgressApiHandler()
  isLoading.value = true

  try {
    const { progressData, periods } = await api.getElementProgressForDescription({
      elementId: props.elementId
    })

    progressDataList.value = normalizeProgressData({
      progressData,
      periods
    })
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

const progressTitles = computed(() => {
  const { progressDescriptionTitle, progressDescriptionTriggerContent } =
    findProgressAggregationOptionsByTypeId({
      typeId: props.progressAggregationTypeId
    })

  return {
    title: t(progressDescriptionTitle),
    triggerText: t(progressDescriptionTriggerContent)
  }
})

const displayProgress = computed(() => {
  return `${
    getSimplifiedNumber(getGradeDisplayValue(props.grade))[PLURALIZATION_KEYS.SIMPLIFIED]
  }${PERCENT}`
})

const { MULTIPLY, DIVIDE, PLUS, PERCENT, APPROXIMATE } = MATH_SYMBOLS

const getPeriodName = (cellData = {}) => {
  return getProgressTableHeaderCellDisplayValue({
    granulationId: props.granulationId,
    cellData
  })
}

const progressDescription = computed(() => {
  if (isNoValidPeriods.value) {
    return {
      keypath: 'okr_element.progress_description.no_data'
    }
  }

  const { progressAggregationTypeId } = props

  if (progressAggregationTypeId === THRESHOLD_PROGRESS_AGGREGATIONS.LAST.value) {
    const lastPeriod = progressDataList.value.at(-1)

    return {
      keypath: 'okr_element.progress_description.last',
      periodName: getPeriodName(lastPeriod)
    }
  }

  if (progressAggregationTypeId === THRESHOLD_PROGRESS_AGGREGATIONS.AVERAGE.value) {
    const totalCount = progressDataList.value.length

    const successCount = progressDataList.value.filter(
      item => item.statusId === ELEMENT_PROGRESS_STATUSES.SUCCEED.statusId
    ).length

    const otherCount = totalCount - successCount

    // ${successCount} out of ${totalCount} periods met the target.

    return {
      totalCount,
      successCount,
      keypath: 'okr_element.progress_description.avg',
      formula: `(${successCount}${MULTIPLY}100 ${PLUS} ${otherCount}${MULTIPLY}0) ${DIVIDE} ${totalCount} ${APPROXIMATE} ${displayProgress.value}`
    }
  }

  if (progressAggregationTypeId === THRESHOLD_PROGRESS_AGGREGATIONS.MIN.value) {
    const firstFailedPeriod =
      progressDataList.value.find(
        item => item.statusId === ELEMENT_PROGRESS_STATUSES.FAILED.statusId
      ) || progressDataList.value[0]

    return {
      keypath: 'okr_element.progress_description.min',
      periodName: getPeriodName(firstFailedPeriod)
    }
  }

  return ''
})

const { t } = useI18n()

const HighlightedText = (_, { slots }) => {
  return h(
    'span',
    {
      style: {
        color: 'var(--dark-1)'
      }
    },
    [h(slots.default)]
  )
}
</script>

<template>
  <AppDroplist
    :disabled
    :hide-on-click="false"
    :theme="`${DROP_LIST_THEMES.NO_SHADOW_NEXT} ${DROP_LIST_THEMES.LIGHT}`"
    append-to=".slu-Main"
    dropdown-width="280px"
    fit-content-width
    placement="bottom"
    trigger="mouseenter"
    @hidden="onDropdownClose"
    @open="getElementProgress"
  >
    <template #button>
      <AppInfoMessage class="pd-Trigger">
        {{ progressTitles.triggerText }}
      </AppInfoMessage>
    </template>

    <div class="pd-Content">
      <div class="pd-Content_Header">
        {{ progressTitles.title }}
      </div>
      <div class="pd-Content_Body">
        <div class="pd-Progress">
          <span> {{ t('okr_element.progress_description.current_progress') }} </span>
          <span
            :style="{
              '--color': `var(${getGradeColorVariable(gradeColor)})`
            }"
            class="pd-Progress_Grade"
          >
            {{ displayProgress }}
          </span>
        </div>

        <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
        <div v-if="isLoading" class="pd-Description pd-Description-blured">
          This text doesn't make any sense.
          <br />
          <HighlightedText>But i'm glad you found it.</HighlightedText>
          Keep going!
        </div>
        <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->

        <template v-else>
          <i18n-t
            v-if="
              progressAggregationTypeId === THRESHOLD_PROGRESS_AGGREGATIONS.AVERAGE.value &&
              !isNoValidPeriods
            "
            class="pd-Description"
            keypath="okr_element.progress_description.avg.summary"
            scope="global"
            tag="div"
          >
            <template #successCount>
              <HighlightedText>
                {{ progressDescription.successCount }}
              </HighlightedText>
            </template>

            <template #totalCount>
              <HighlightedText>
                {{ progressDescription.totalCount }}
              </HighlightedText>
            </template>
          </i18n-t>

          <i18n-t
            :keypath="progressDescription.keypath"
            class="pd-Description"
            scope="global"
            tag="div"
          >
            <template #periodName>
              <HighlightedText>{{ progressDescription.periodName }}</HighlightedText>
            </template>
            <template #formula>
              <HighlightedText>{{ progressDescription.formula }}</HighlightedText>
            </template>
          </i18n-t>
        </template>
      </div>
      <div class="pd-Content_Footer">
        <AppHrefLink href="https://oboard.atlassian.net/wiki/x/B4CqZ" prepend-icon="guide">
          {{ t('support.help_center.link.text') }}
        </AppHrefLink>
      </div>
    </div>
  </AppDroplist>
</template>

<style lang="scss" scoped>
.pd-Trigger {
  --layout-gap: 4px;
  // --color: #{$dark-1};
  --color: #{$dark-3};
  --icon-color: #{$dark-3};
  --font-weight: #{fw('semi-bold')};
}

.pd-Content {
  width: 280px;
  white-space: normal;
  border-radius: inherit;
  overflow: hidden;
}

.pd-Content_Body {
  padding: 10px 20px 16px;
}

.pd-Content_Header {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 20px;
  font-weight: fw('semi-bold');
  padding: 16px 20px 10px;
}

.pd-Progress {
  font-size: $fs-12;
  line-height: 20px;
  font-weight: fw('semi-bold');
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: space-between;
  color: $dark-3;
  margin-bottom: 16px;
}

.pd-Progress_Grade {
  font-size: $fs-14;
  color: var(--color);
  font-weight: fw('bold');
}

.pd-Description {
  font-size: $fs-12;
  line-height: 20px;
  font-weight: fw('semi-bold');
  color: $dark-3;
  white-space: pre-line;

  &-blured {
    filter: blur(3px);
  }

  &:not(&:last-child) {
    margin-bottom: 8px;
  }
}

.pd-Content_Footer {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $grey-3-next;
}
</style>
